import { useState, type FormEvent } from 'react'
import { Loader2 } from 'lucide-react'
import { Seo } from '@/components/Seo'
import { PageHeader } from '@/components/Page'
import { FormError } from '@/components/AuthShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { toast } from '@/components/ui/sonner'
import { useSession } from '@/lib/session'
import { api } from '@/lib/api'
import { useI18n, translateError } from '@/i18n'

/**
 * THE ACCOUNT PAGE — the scaffold's first page that requires signing in.
 *
 * It exists to prove the loop is closed: register → session → guarded route →
 * read and write your own data → sign out. Copy this shape when building a new
 * page that needs an account.
 */
export default function Account() {
  const { user, isAdmin, refresh, logout } = useSession()
  const { t } = useI18n()

  return (
    <>
      <Seo title={t('account.title')} noIndex />
      <PageHeader
        eyebrow={t('account.eyebrow')}
        title={t('account.title')}
        body={t('account.body')}
        actions={
          <>
            {isAdmin && (
              <Badge variant="outline" className="font-normal">{t('account.adminBadge')}</Badge>
            )}
            <Button variant="outline" onClick={() => void logout()}>
              {t('account.signOut')}
            </Button>
          </>
        }
      />

      <div data-upbo-src="src/pages/Account.tsx:46" className="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6 sm:py-16">
        <div data-upbo-src="src/pages/Account.tsx:47" className="flex flex-col gap-6">
          <ProfileCard initialName={user?.name ?? ''} onSaved={refresh} />
          <PasswordCard />
        </div>
      </div>
    </>
  )
}

function ProfileCard({ initialName, onSaved }: { initialName: string; onSaved: () => Promise<void> }) {
  const { t } = useI18n()
  const [name, setName] = useState(initialName)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await api.auth.updateProfile(name)
      await onSaved()
      toast.success(t('account.profile.saved'))
    } catch (err) {
      setError(translateError(err, t, t('account.profile.failed')))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card className="p-6 sm:p-8">
      <CardHeader>
        <CardTitle className="text-base font-medium">{t('account.profile.title')}</CardTitle>
        <CardDescription>{t('account.profile.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form data-upbo-src="src/pages/Account.tsx:84" onSubmit={submit} className="flex max-w-sm flex-col gap-4">
          <FormError message={error} />
          <div data-upbo-src="src/pages/Account.tsx:86" className="flex flex-col gap-2">
            <Label htmlFor="display-name">{t('field.displayName')}</Label>
            <Input id="display-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <Button type="submit" disabled={busy} className="self-start">
            {busy && <Loader2 className="size-4 animate-spin" />}
            {t('account.save')}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

function PasswordCard() {
  const { t } = useI18n()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await api.auth.changePassword(current, next)
      setCurrent('')
      setNext('')
      toast.success(t('account.password.changed'))
    } catch (err) {
      setError(translateError(err, t, t('account.password.failed')))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card className="p-6 sm:p-8">
      <CardHeader>
        <CardTitle className="text-base font-medium">{t('account.password.title')}</CardTitle>
        <CardDescription>{t('account.password.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form data-upbo-src="src/pages/Account.tsx:130" onSubmit={submit} className="flex max-w-sm flex-col gap-4">
          <FormError message={error} />
          <div data-upbo-src="src/pages/Account.tsx:132" className="flex flex-col gap-2">
            <Label htmlFor="current-password">{t('field.currentPassword')}</Label>
            <Input
              id="current-password"
              type="password"
              autoComplete="current-password"
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </div>
          <div data-upbo-src="src/pages/Account.tsx:143" className="flex flex-col gap-2">
            <Label htmlFor="new-password">{t('field.newPassword')}</Label>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={busy} className="self-start">
            {busy && <Loader2 className="size-4 animate-spin" />}
            {t('account.password.submit')}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
